import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'

/**
 * One vocabulary for every state the platform reports.
 *
 * Shared verbatim with the operator console. A customer reading «فعال» on their
 * application and an operator reading «فعال» on the same row are reading the
 * same word about the same fact, which is the point — two vocabularies for one
 * set of states is how a support conversation starts with both sides describing
 * different things.
 *
 * Before this, each page invented its own wording: a disabled route said
 * "disabled" on one screen and "inactive" on another, and readiness spoke in
 * enum names. An operator was left deciding whether two different words meant
 * two different things. They did not.
 *
 * The technical value stays reachable — it is what appears in a support
 * conversation or a log grep — but as a tooltip, not as the label.
 */
type Tone = 'ok' | 'warn' | 'bad' | 'idle' | 'busy'

interface Entry {
  label: string
  tone: Tone
}

const VOCABULARY: Record<string, Entry> = {
  // lifecycle
  active: { label: 'فعال', tone: 'ok' },
  enabled: { label: 'فعال', tone: 'ok' },
  inactive: { label: 'غیرفعال', tone: 'idle' },
  disabled: { label: 'غیرفعال', tone: 'idle' },
  draft: { label: 'پیش‌نویس', tone: 'idle' },
  suspended: { label: 'معلق', tone: 'warn' },
  revoked: { label: 'باطل‌شده', tone: 'bad' },

  // readiness
  ready: { label: 'آماده', tone: 'ok' },
  blocked: { label: 'نیازمند تنظیم', tone: 'warn' },

  // request and attempt outcomes
  received: { label: 'ثبت‌شده', tone: 'busy' },
  rejected: { label: 'ردشده', tone: 'bad' },
  timeout: { label: 'وقت تمام', tone: 'bad' },
  error: { label: 'خطا', tone: 'bad' },
  // A recharge that has been asked for and not yet settled. «در انتظار» would
  // read as our delay; «در انتظار تأیید» says what is actually being waited on.
  settled: { label: 'تسویه‌شده', tone: 'ok' },
  succeeded: { label: 'موفق', tone: 'ok' },
  success: { label: 'موفق', tone: 'ok' },
  failed: { label: 'ناموفق', tone: 'bad' },
  failure: { label: 'ناموفق', tone: 'bad' },
  pending: { label: 'در انتظار تأیید', tone: 'busy' },
  in_progress: { label: 'در حال انجام', tone: 'busy' },
  refunded: { label: 'بازگشت وجه', tone: 'warn' },
  released: { label: 'آزادشده', tone: 'idle' },
  captured: { label: 'برداشت‌شده', tone: 'ok' },
  // Neither موفق nor ناموفق, and deliberately not either: the platform lost
  // track of an استعلام that may have been انجام شده, and is finding out. What
  // the customer needs to know is on the row beside it — مبلغی کسر نشده — so
  // the label says what is happening rather than guessing an outcome.
  under_review: { label: 'در حال بررسی', tone: 'busy' },
}

const TONE_COLOUR = {
  ok: 'success',
  warn: 'warning',
  bad: 'error',
  idle: 'default',
  busy: 'info',
} as const

export function StatusChip({ status }: { status: string | null | undefined }) {
  if (!status) {
    return <Chip size="small" variant="outlined" label="نامشخص" />
  }

  const key = status.toLowerCase()
  const entry = VOCABULARY[key]

  // An unmapped status shows its raw value rather than a guess. Inventing a
  // Persian word for a state nobody has classified would be worse than
  // admitting the console does not know it yet.
  if (!entry) {
    return <Chip size="small" variant="outlined" label={status} sx={{ fontFamily: 'monospace' }} />
  }

  return (
    <Tooltip title={`کد فنی: ${status}`}>
      <Chip size="small" color={TONE_COLOUR[entry.tone]} label={entry.label} />
    </Tooltip>
  )
}

/** The Persian label alone, for places a chip would be too heavy. */
export function statusLabel(status: string | null | undefined): string {
  if (!status) return 'نامشخص'
  return VOCABULARY[status.toLowerCase()]?.label ?? status
}
